import React from "react";
import { motion } from "framer-motion";
import { useIntersectionObserver } from "@/hooks/useIntersectionObserver";
import SkillsVisualization from "./SkillsVisualization";
import InteractiveTimeline from "./InteractiveTimeline";
import TechStack3D from "./TechStack3D";

const AboutSection = () => {
  const { elementRef, isIntersecting } = useIntersectionObserver({
    threshold: 0.01, // Reduced from 0.1 to trigger earlier on mobile
    rootMargin: "0px 0px -50px 0px", // Trigger 50px before entering viewport
    triggerOnce: true,
  });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        duration: 0.6,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <section
      id="about"
      className="section-padding bg-gray-50 dark:bg-dark-800 relative overflow-hidden"
      ref={elementRef}
    >
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500 rounded-full filter blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-accent-500 rounded-full filter blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isIntersecting ? "visible" : "hidden"}
          className="max-w-6xl mx-auto"
        >
          {/* Section Header */}
          <motion.div variants={itemVariants} className="text-center mb-16">
            <motion.span
              className="text-primary-500 font-medium text-lg mb-4 block"
              variants={itemVariants}
            >
              Get to know me
            </motion.span>
            <motion.h2
              className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold mb-6"
              variants={itemVariants}
            >
              About <span className="text-primary-500">Me</span>
            </motion.h2>
            <motion.p
              className="text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto"
              variants={itemVariants}
            >
              I build products, teach robotics, shoot and edit video, and write about tech. Here's my story.
            </motion.p>
          </motion.div>

          {/* Main Content Grid */}
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-20">
            {/* Left Column - Text Content */}
            <motion.div variants={itemVariants} className="space-y-6 text-center md:text-left">
              <div className="space-y-5">
                <div>
                  <h3 className="text-2xl md:text-3xl font-heading font-semibold text-gray-900 dark:text-white">
                    I'm Boye Emmanuel Tete.
                  </h3>
                  <p className="mt-2 text-2xl md:text-3xl font-heading font-bold tracking-tight">
                    <span className="relative inline-block">
                      <span className="relative z-10 text-emerald-500">
                        I build, teach, and ship.
                      </span>
                      <span className="absolute left-0 bottom-0.5 w-full h-[3px] bg-emerald-500 rounded-full opacity-60" />
                    </span>
                  </p>
                </div>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed text-justify md:text-left">
                  I graduated with a B.Sc. in Computer Science from All Nations University in Koforidua, Ghana, in November 2025. During those four years I wasn't just studying. I was building: data platforms, AI tools, SMS gateways, SaaS products. By the time I walked out, I had 32+ projects live and paying clients already on board.
                </p>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed text-justify md:text-left">
                  Today I run{" "}
                  <a
                    href="https://www.google.com/search?q=tboye+creative+solutions&oq=tboye+creative+solutions+&gs_lcrp=EgZjaHJvbWUyBggAEEUYOTIGCAEQRRg7MgYIAhBFGDsyBggDEEUYOzIGCAQQRRg9MgYIBRBFGD3SAQg1OTMxajBqN6gCALACAA&sourceid=chrome&source=chrome.ob&ie=UTF-8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-emerald-600 dark:text-emerald-400 hover:underline underline-offset-2"
                  >
                    Tboye Creative Solutions
                  </a>
                  , teach STEM robotics to kids across international schools in Accra, and keep building products. I also blog at{" "}
                  <a
                    href="https://tboyemedia.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-emerald-600 dark:text-emerald-400 hover:underline underline-offset-2"
                  >
                    TboyeMedia
                  </a>
                  {" "}— tech, AI, building in Africa, and everything in between. On the creative side, I do videography and video editing — Premiere Pro, CapCut, the works. I work across the full stack: React, Node.js, Python, Cloudflare, AI/ML. Whatever the job needs.
                </p>
                <p className="text-lg text-gray-600 dark:text-gray-400 leading-relaxed text-justify md:text-left">
                  I'm based in Ghana and I work with clients globally. If you need something built properly, I reply within 24 hours.
                </p>
              </div>

            </motion.div>

            {/* Right Column - Profile Image */}
            <motion.div variants={itemVariants} className="relative">
              <div className="relative max-w-md mx-auto">
                <div className="relative overflow-hidden rounded-2xl shadow-2xl border border-emerald-500/20">
                  <img
                    src="https://i.postimg.cc/HxcyH2SQ/tboye.jpg"
                    alt="Boye Emmanuel"
                    className="w-full h-auto object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30" />
                </div>
                {/* Single clean accent line */}
                <div className="absolute -bottom-3 -right-3 w-full h-full rounded-2xl border-2 border-emerald-500/30 -z-10" />
              </div>
            </motion.div>
          </div>

          {/* Skills Section */}
          <motion.div variants={itemVariants} className="mb-20">
            <h3 className="text-3xl font-heading font-bold text-center mb-12">
              Skills & <span className="text-primary-500">Expertise</span>
            </h3>
            <SkillsVisualization />
          </motion.div>

          {/* 3D Tech Stack */}
          <motion.div variants={itemVariants} className="mb-20">
            <h3 className="text-3xl font-heading font-bold text-center mb-12">
              Tech <span className="text-primary-500">Stack</span>
            </h3>
            <TechStack3D />
          </motion.div>

          {/* Experience Timeline */}
          <motion.div variants={itemVariants}>
            <h3 className="text-3xl font-heading font-bold text-center mb-12">
              My <span className="text-primary-500">Journey</span>
            </h3>
            <InteractiveTimeline />
          </motion.div>

          {/* Call to Action */}
          <motion.div variants={itemVariants} className="text-center mt-16">
            <motion.button
              className="px-8 py-4 bg-primary-500 text-white font-semibold rounded-full hover:shadow-lg transition-all duration-300"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.98 }}
              onClick={() =>
                document
                  .getElementById("contact")
                  .scrollIntoView({ behavior: "smooth" })
              }
            >
              Let's Work Together
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
